import { notifyAuthenticatedUserChanged } from "../utils/native/appLockAuthBridge";

/**
 * Derives the `AUTHENTICATED_USER_CHANGED` signal from the existing `auth`
 * Redux slice by subscribing to the store, rather than instrumenting each
 * login/logout call site. Every place that already sets
 * `isAuthenticated`/`isLoading` is covered automatically, and the emitted
 * signal can never diverge from what the app's own UI is already showing.
 *
 * Never emits while `isLoading` is `true` (auth not yet resolved). Once
 * resolved, `isAuthenticated` is the conclusive signal — this includes the
 * case where `authSlice.js` intentionally keeps `isAuthenticated: true`
 * while the session-expired modal offers in-place re-auth, so this module
 * only emits `false` when the rest of the app also treats the session as
 * over, never on the first transient 401.
 */

/** Last emitted boolean, or `null` if nothing has been emitted yet. */
let lastEmittedAuthenticated = null;

/** Pure: returns `null` while still loading/hydrating (no conclusive resolution yet). */
export function resolveAuthenticatedFromState(state) {
  const authState = state?.auth;
  if (!authState || authState.isLoading) {
    return null;
  }
  return Boolean(authState.isAuthenticated);
}

/** Installs the store subscription and returns Redux's `unsubscribe`. */
export function installAppLockAuthStateBridge(store) {
  function handleStoreChange() {
    const resolved = resolveAuthenticatedFromState(store.getState());
    if (resolved === null || resolved === lastEmittedAuthenticated) {
      return;
    }
    lastEmittedAuthenticated = resolved;
    notifyAuthenticatedUserChanged(resolved);
  }

  const unsubscribe = store.subscribe(handleStoreChange);
  // store.subscribe only fires on a future dispatch; run once immediately so
  // this is correct even if installed against a store whose state is already
  // resolved.
  handleStoreChange();
  return unsubscribe;
}

/** Test-only: never called from production code. */
export function __resetAppLockAuthStateBridgeForTests() {
  lastEmittedAuthenticated = null;
}
